import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  City,
  CivilStatus,
  Currency,
  DataType,
  DataTypeOptions,
  Days,
  IdentityDocumentOptions,
  Employment,
  EmploymentRelationship,
  Gender,
  Insurance,
  Month
} from '@core/constants/data-types.constants';
import { Configurations, SelectionOption } from '@core/models/coinscrap/configurations.model';
import { extractText } from '@core/models/coinscrap/i18n-message';
import { monetaryAmountToCurrencyAmount } from '@core/models/coinscrap/monetary-amount.model';
import {
  OfferingProductConfiguration,
  OfferingProductParams,
  OfferingProductsDefinition
} from '@core/models/coinscrap/offering-product-definition.model';
import {
  AgeMilestones,
  Config,
  DefaultConfig,
  IdentityDocumentConstraints,
  UniversitiesByLocation
} from '@core/models/config.model';
import { CurrencyAmount } from '@core/models/currency-amount.model';
import { CurrencyExchange } from '@core/models/currency-exchange';
import { Increase } from '@core/models/increase.model';
import { InvestmentData } from '@core/models/investment.model';
import { ProductCoverage } from '@core/models/product-coverage.model';
import { ProductLabels } from '@core/models/product-labels.model';
import { IProduct, InvestmentProduct, NormalProduct, Product } from '@core/models/product.model';
import { environment } from '@environment';
import { LanguageService } from '@i18n/services/language.service';
import { Store } from '@ngrx/store';
import * as actions from '@redux/actions';
import { Nullable } from '@shared/utils/mapped-types.utils';
import { tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class ConfigService {
  config!: Config;

  constructor(private store: Store, private http: HttpClient, private languageService: LanguageService) {}

  getDefaultCurrencyAmount(): Nullable<CurrencyAmount> {
    return {
      currency: this.config.defaultCurrency,
      amount: null
    };
  }

  updateConfig() {
    return this.http.get<Configurations>(`${environment.scorings.url}/configurations/scorings`).pipe(
      tap(configurations => {
        this.config = this.mapConfigurationsToConfig(configurations);
        this.store.dispatch(actions.fullUpdateConfig({ newConfig: this.config }));
      })
    );
  }

  mapConfigurationsToConfig(configurations: Configurations): Config {
    const config: Config = { ...DefaultConfig };
    config.sourceConfigurations = configurations;
    const currentLang = this.languageService.currentLang;
    // Validate configurations
    if (configurations.currencyOptions.length === 0) {
      throw new Error('There are no currency options');
    }
    // Contact data
    if (configurations.contactData) {
      config.contactData = {
        email: configurations.contactData.email,
        phone: configurations.contactData.phone
      };
    }
    // Age milestones
    config.ageMilestones = this.extractAgeMilestones(configurations);
    // Currency options
    config.defaultCurrency =
      configurations.currencyOptions.find(c => c.isDefault)?.identifier ||
      configurations.defaultCurrency ||
      configurations.currencyOptions[0].identifier;
    config.currencies = this.mapOptions<Currency>(configurations.currencyOptions, currentLang);
    config.currencyExchange = this.extractCurrencyExchange(configurations, config.currencies);
    config.currencyPosition = configurations.i18n.currency.symbolPosition;
    config.days = Days;
    // Select options
    if (configurations.identityDocumentOptions) {
      config.identityDocumentOptions = this.mapOptions<IdentityDocumentOptions>(
        configurations.identityDocumentOptions,
        currentLang
      );
      config.identityDocumentConstraints = this.mapIdentityDocumentConstraints(configurations.identityDocumentOptions);
    }
    config.genders = this.mapOptions<Gender>(configurations.genreOptions, currentLang);
    config.civilStatuses = this.mapOptions<CivilStatus>(configurations.civilStatusOptions, currentLang);
    config.universities = this.mapOptions<City>(configurations.universitiesOptions, currentLang);
    config.universitiesByLocation = this.extractUniversitiesByLocation(configurations, currentLang, config.currencies);
    config.employmentRelationships = this.mapOptions<EmploymentRelationship>(
      configurations.employmentStatusOptions,
      currentLang
    );
    config.employments = this.mapOptions<Employment>(configurations.ocupationsOptions, currentLang);
    config.monthlyExpenditures = this.mapOptions(configurations.expenseConceptOptions, currentLang);
    config.savingsAndInvestments = this.mapOptions(configurations.investmentPurposeOptions, currentLang);
    config.investorProfiles = this.mapOptions(configurations.investorProfileOptions, currentLang);
    config.futurePlans = this.mapOptions(configurations.futureGoalPurposeOptions, currentLang);
    config.loans = this.mapOptions(configurations.debtSourceOptions, currentLang);
    config.products = this.extractProductConfiguration(configurations.offeringProductsDefinitions, currentLang);
    if (configurations.monthsOptions) {
      config.months = this.mapOptions<Month>(configurations.monthsOptions, currentLang, this.transformToNumber);
    }
    if (configurations.frequenciesOptions) {
      config.frequencies = this.mapOptions(configurations.frequenciesOptions, currentLang);
    }
    return config;
  }

  private mapOptionDirectlyToValue(options: SelectionOption[], language: string): DataTypeOptions<string> {
    const opts: DataTypeOptions<string> = [];
    options.forEach(opt => {
      opts.push({
        key: extractText(opt.message, language),
        value: opt.identifier
      });
    });
    return opts;
  }

  private mapIdentityDocumentConstraints(options: SelectionOption[]): IdentityDocumentConstraints[] {
    const identityDocumentsConstraints: IdentityDocumentConstraints[] = [];
    options.forEach(opt => {
      identityDocumentsConstraints.push({
        key: opt.identifier,
        mask: opt.params.constraintsJSON.mask,
        identityDocumentValidator: new RegExp(opt.params.constraintsJSON.validator)
      });
    });
    return identityDocumentsConstraints;
  }

  mapOptions<T extends DataType>(
    options: SelectionOption[],
    language: string,
    fn: (opts: DataTypeOptions<T>) => DataTypeOptions<T> = o => o
  ): DataTypeOptions<T> {
    const opts: DataTypeOptions<T> = this.mapOptionDirectlyToValue(options, language) as DataTypeOptions<T>;
    return fn(opts);
  }

  transformToNumber<T extends DataType>(options: DataTypeOptions<T>) {
    return options.map(option => {
      return {
        key: option.key,
        value: +option.value
      };
    });
  }

  extractAgeMilestones(configurations: Configurations): AgeMilestones {
    const configurationsAgeMilestones: Partial<AgeMilestones> = {
      retirement: configurations.defaultRetirementParams.estimatedRetirementAge,
      university: configurations.childsEducationSavingsPredictionParams.universityStartAge
    };
    const defaultMixedConfiguration: AgeMilestones = { ...DefaultConfig.ageMilestones, ...configurationsAgeMilestones };
    defaultMixedConfiguration.childMaxAge = Math.min(
      defaultMixedConfiguration.university,
      defaultMixedConfiguration.childMaxAge
    );
    return defaultMixedConfiguration;
  }

  extractCurrencyExchange(configurations: Configurations, currencies: DataTypeOptions<Currency>): CurrencyExchange {
    const currencyExchange: CurrencyExchange = {};
    // Calculate default currency, which we have directly
    const defaultTo: {
      [key: string]: number;
    } = {};
    currencies.forEach(c => {
      const mEx = configurations.monetaryExchanges.find(m => m.currencyFrom === c.value);
      if (c.value === configurations.defaultCurrency) {
        defaultTo[c.value] = 1;
      } else if (mEx) {
        // Since we get defaultCurrency by multiplying otherCurrency by [this rate],
        // then we get otherCurrency by multiplying defaultCurrency by [1 / this rate]
        defaultTo[c.value] = 1 / mEx.rate;
      }
    });
    currencyExchange[configurations.defaultCurrency] = {
      to: defaultTo
    };

    // Calculate the rest of the currencies, which we will have only its self
    // transformation (1) and the one with the default currency. For the other
    // currencies, we will need to transform to the default currency and then
    // to the other currency. Be aware that transformations could suffer loss
    // of precission when transforming to other currencies repeatedly and then
    // returning to the first one.
    configurations.monetaryExchanges.forEach(m => {
      const to: {
        [key: string]: number;
      } = {};
      currencies.forEach(c => {
        if (c.value === m.currencyFrom) {
          to[c.value] = 1;
        } else if (c.value === m.currencyTo) {
          to[c.value] = m.rate;
        } else {
          to[c.value] = m.rate * (defaultTo[c.value] || 0);
        }
      });
      currencyExchange[m.currencyFrom] = {
        to
      };
    });

    return currencyExchange;
  }

  extractUniversitiesByLocation(
    configurations: Configurations,
    language: string,
    currencies: DataTypeOptions<Currency>
  ): UniversitiesByLocation {
    return configurations.universitiesOptions.reduce<UniversitiesByLocation>((r, p) => {
      const region = p.params.regionMessage;
      r[region] = r[region] || [];
      r[region].push({
        option: { key: extractText(p.message, language), value: p.identifier },
        params: {
          universityCost: monetaryAmountToCurrencyAmount(p.params.priceAmount, currencies)
        }
      });
      return r;
    }, Object.create(null));
  }

  extractProductConfiguration(productDefinitions: OfferingProductsDefinition[], language: string) {
    const groupedProductDefinitionsById = productDefinitions.reduce((r, p) => {
      if (!p.identifier) {
        throw new Error('Product identifier not found or is empty!');
      }
      r[p.identifier] = p;
      return r;
    }, Object.create(null));
    const products = Object.create(null) as {
      [key: string]: Product;
    };
    Object.entries<OfferingProductsDefinition>(groupedProductDefinitionsById).forEach(e => {
      const defaultOfferingProduct = e[1];
      // Common fields: Id and labels
      const labels = defaultOfferingProduct.labels || {};
      const productCurrencies = this.getProductCurrencies(defaultOfferingProduct.configurations);
      const selectedCurrency = productCurrencies[0];
      const params = defaultOfferingProduct.params;
      const insurance = this.insuranceFromPurpose(defaultOfferingProduct.purpose);
      if (!params) {
        throw new Error('Cannot extract offering product definition parameters');
      }
      const productLabels: ProductLabels = {
        name: defaultOfferingProduct.name || '',
        description: labels.description ? extractText(labels.description, language) : '',
        cardDescription: labels.cardDescription ? extractText(labels.cardDescription, language) : '',
        cardSubtitle: labels.cardSubtitle ? extractText(labels.cardSubtitle, language) : '',
        cardUpperLabel: labels.cardUpperLabel ? extractText(labels.cardUpperLabel, language) : '',
        customizationTitle: labels.customizationTitle ? extractText(labels.customizationTitle, language) : ''
      };
      const commonProduct: IProduct = {
        type: params.tarificationFormat || 'normal',
        insurance: insurance || 'DEATH',
        id: defaultOfferingProduct.identifier || '',
        liteId: params.tarificationId || '',
        labels: productLabels,
        allowedCurrencies: productCurrencies,
        selectedCurrency: selectedCurrency,
        temporalityConditions: [],
        coverages: this.getProductCoverages(defaultOfferingProduct, language),
        allowedIncreases: params.increaseJSON,
        selectedIncrease: this.getSelectedIncrease(params, selectedCurrency),
        cartStatus: {}
      };
      let product: Product;
      // Type dependant limits and parameters
      switch (commonProduct.type) {
        case 'investment':
          product = commonProduct as InvestmentProduct;
          break;
        case 'normal':
          product = commonProduct as NormalProduct;
          break;
      }
      products[e[0]] = product;
    });
    return products;
  }

  private getProductCurrencies(productConfigurations?: OfferingProductConfiguration[]): Currency[] {
    const currencySet = new Set<Currency>();
    productConfigurations?.forEach(p => {
      if (p.value) {
        currencySet.add(p.value.currency);
      }
    });
    return Array.from(currencySet);
  }

  private getProductCoverages(productDefinition: OfferingProductsDefinition, language: string): ProductCoverage[] {
    const coveragesMap = new Map<string, ProductCoverage>();
    productDefinition.params?.coveragesJSON?.forEach(c => {
      coveragesMap.set(c.identifier, {
        id: c.identifier,
        name: c.name,
        description: extractText(c.description, language),
        percentageText: c.percentageText ? extractText(c.percentageText, language) : '',
        type: c.type,
        valueType: c.valueType ?? 'number',
        affectsOthers: c.affectsOthers || false,
        isAmountModifiable: c.amountLimits ? true : false,
        amountLimits: c.amountLimits,
        condition: c.condition,
        percentageLimits: c.percentageLimits,
        valid: true,
        active: c.type === 'included',
        currentAmount: c.amount ? { amount: c.amount.value, currency: c.amount.currency } : undefined,
        amountText: c.amountText,
        group: c.group
      });
    });
    return Array.from(coveragesMap.values());
  }

  private getProjectedInvestment(params: OfferingProductParams): InvestmentData | undefined {
    const investment = params.projectedInvestmentJSON;
    if (investment) {
      return {
        id: investment.id,
        description: investment.description,
        projectionValues: investment.projectionValues,
        projectionPlaceholder: investment.projectionPlaceholder,
        active: false,
        valid: true
      };
    }
    return undefined;
  }

  private getSelectedIncrease(params: OfferingProductParams, currency: Currency): Increase | undefined {
    const allowedIncreases = params.increaseJSON;
    if (allowedIncreases && allowedIncreases.length > 0) {
      return allowedIncreases.filter(increase => {
        return increase.currency === currency;
      })[0];
    }
    return undefined;
  }

  private insuranceFromPurpose(purpose: string | undefined): Insurance | undefined {
    if (purpose) {
      switch (purpose.split('/')[0]) {
        case 'death':
          return 'DEATH';
        case 'education':
          return 'EDUCATION';
        case 'futureGoals':
          return 'SAVING';
        case 'retirement':
          return 'RETIREMENT';
      }
    }
    return undefined;
  }
}
